import * as Blockly from 'blockly/core'
import { LuaGenerator, Order } from 'blockly/lua'
import { BlockDefinition, statementBlock, untypedOutput } from './common'

export const VARIABLE_CATEGORY = 'VARIABLES'
export const MAKE_VARIABLE_BUTTON_KEY = 'MAKE_VARIABLE'

class FieldAlphanumericVariable extends Blockly.FieldVariable {
  onItemSelected_(menu: Blockly.Menu, menuItem: Blockly.MenuItem) {
    const variable = this.getVariable() ?? undefined
    if (variable && menuItem.getValue() === Blockly.RENAME_VARIABLE_ID) {
      let workspace = this.sourceBlock_?.workspace as Blockly.WorkspaceSvg
      // If the block is in a flyout, get the target workspace.
      workspace = workspace?.targetWorkspace ?? workspace
      workspace?.fireChangeListener(
        new RequestVarName(
          RequestVarNameType.RENAME,
          (name) => {
            workspace.renameVariableById(variable?.getId() ?? '', name)
          },
          workspace,
          variable,
        ),
      )
      return
    }
    super.onItemSelected_(menu, menuItem)
  }
}
Blockly.fieldRegistry.register(
  'field_alphanumeric_variable',
  FieldAlphanumericVariable,
)

export const variableDefaults: Partial<BlockDefinition> = {
  style: 'variables',
}

export const variableBlocks: Array<BlockDefinition> = [
  {
    opcode: 'variable_getVariable',
    ...untypedOutput,
    args0: [
      {
        type: 'field_alphanumeric_variable',
        name: 'VAR',
        variable: '%{BKY_VARIABLES_DEFAULT_NAME}',
      },
    ],
    generator: (block: Blockly.Block) => {
      const n = block.getField('VAR')?.getText()
      return [`${n}`, Order.ATOMIC]
    },
    message0: '%1',
  },
  {
    opcode: 'variable_setVariableTo',
    ...statementBlock,
    args0: [
      {
        type: 'field_alphanumeric_variable',
        name: 'VAR',
        variable: '%{BKY_VARIABLES_DEFAULT_NAME}',
      },
      {
        type: 'input_value', // This expects an input of any type
        name: 'VAL',
      },
    ],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = block.getField('VAR')?.getText()
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return `${n} = ${v}\n`
    },
    message0: '%{BKY_B_SET_VARIABLE_TO}',
  },
  {
    opcode: 'variable_changeVariableBy',
    ...statementBlock,
    args0: [
      {
        type: 'field_alphanumeric_variable',
        name: 'VAR',
        variable: '%{BKY_VARIABLES_DEFAULT_NAME}',
      },
      {
        type: 'input_value',
        name: 'VAL',
      },
    ],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = block.getField('VAR')?.getText()
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return `${n} += ${v}\n`
    },
    message0: '%{BKY_B_CHANGE_VARIABLE_BY}',
  },
]

export const generateVariablePreamble = (workspace: Blockly.Workspace) => {
  let preamble = workspace
    .getAllVariables()
    .map((variable) => {
      return `local ${variable.name} = 0`
    })
    .join('\n')
  if (preamble) {
    preamble += '\n\n'
  }
  return preamble
}

const variablesFlyoutCallback = (workspace: Blockly.Workspace) => {
  const variables = workspace.getAllVariables()
  const blockList: Blockly.utils.toolbox.FlyoutDefinition = variables.map(
    (variable) => ({
      kind: 'block',
      type: 'variable_getVariable',
      fields: {
        VAR: variable,
      },
    }),
  )

  blockList.unshift({
    kind: 'button',
    text: '%{BKY_B_MAKE_NEW_VARIABLE}',
    callbackkey: MAKE_VARIABLE_BUTTON_KEY,
  })

  if (variables.length > 0) {
    blockList.push(
      {
        kind: 'block',
        type: 'variable_setVariableTo',
        fields: {
          VAR: variables[variables.length - 1],
        },
        inputs: {
          VAL: {
            shadow: {
              type: 'math_number',
              fields: {
                NUM: '0',
              },
            },
          },
        },
      },
      {
        kind: 'block',
        type: 'variable_changeVariableBy',
        fields: {
          VAR: variables[variables.length - 1],
        },
        inputs: {
          VAL: {
            shadow: {
              type: 'math_number',
              fields: {
                NUM: '1',
              },
            },
          },
        },
      },
    )
  }

  return blockList
}

export enum RequestVarNameType {
  CREATE = 'request_var_create',
  RENAME = 'request_var_rename',
}

export class RequestVarName extends Blockly.Events.VarBase {
  constructor(
    public type: RequestVarNameType,
    public callback: (name: string) => void,
    public workspace: Blockly.Workspace,
    public variable?: Blockly.VariableModel,
  ) {
    super(variable)
  }
}

const makeVariableButtonCallback = (button: Blockly.FlyoutButton) => {
  const workspace = button.getTargetWorkspace()
  const callback = (name: string) => {
    workspace.createVariable(name)
  }
  workspace.fireChangeListener(
    new RequestVarName(RequestVarNameType.CREATE, callback, workspace),
  )
}

export const registerVariableCallbacks = (workspace: Blockly.WorkspaceSvg) => {
  workspace.registerToolboxCategoryCallback(
    VARIABLE_CATEGORY,
    variablesFlyoutCallback,
  )
  workspace.registerButtonCallback(
    MAKE_VARIABLE_BUTTON_KEY,
    makeVariableButtonCallback,
  )
}
